import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Clock, Shield, Phone } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/shared/SEO';
import { ContactForm } from '../components/forms/ContactForm';
import { SERVICE_AREAS, COMPANY_INFO, getServiceById } from '../data/mock';

export default function ContactPage() {
  const { serviceId } = useParams();
  const service = serviceId ? getServiceById(serviceId) : null;

  return (
    <Layout>
      <SEO
        title="Contact Sprinkler Repair | AAA Irrigation | Allen, TX"
        description="Contact AAA Irrigation Service for sprinkler repair in Allen, Plano, McKinney, Frisco TX. TCEQ Licensed. Call (469) 751-3567 or request service online."
        canonical="https://aaairrigationservice.com/contact"
        keywords="contact irrigation repair Allen TX, sprinkler repair quote Plano, irrigation service request McKinney TX, TCEQ licensed irrigator Frisco, sprinkler repair near me"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <a 
              href={COMPANY_INFO.licenseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-1.5 mb-6 hover:bg-teal-500/20 transition-colors"
            >
              <Shield className="w-4 h-4 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">TCEQ Licensed: {COMPANY_INFO.license}</span>
            </a>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contact Us
            </h1>
            <p className="text-lg text-slate-300">
              Get in touch for professional irrigation services. We'll respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <ContactForm preselectedService={service?.title || ''} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Contact Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Contact</h3>
                <div className="space-y-4">
                  <a
                    href={`tel:${COMPANY_INFO.phone}`}
                    className="flex items-center gap-3 text-slate-700 hover:text-teal-600 transition-colors"
                  >
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{COMPANY_INFO.phoneFormatted}</p>
                      <p className="text-sm text-slate-500">Call or Text</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Hours Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-teal-500" />
                  Business Hours
                </h3>
                <div className="space-y-2 text-slate-600">
                  <p>{COMPANY_INFO.hours.weekday}</p>
                  <p>{COMPANY_INFO.hours.saturday}</p>
                  <p className="text-amber-600 font-medium">{COMPANY_INFO.hours.sunday}</p>
                </div>
              </div>

              {/* Service Areas Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-500" />
                  Service Areas
                </h3>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_AREAS.map((area) => (
                    <Link
                      key={area}
                      to={`/locations/${area.toLowerCase()}`}
                      className="inline-block bg-slate-100 hover:bg-teal-50 text-slate-700 hover:text-teal-700 text-sm px-3 py-1 rounded-full transition-colors"
                    >
                      {area}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
